import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Link, useNavigate, useParams} from "react-router-dom";
import {GitBranch} from "lucide-react";
import {mapApiError} from "../../shared/api/errors";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {ADMIN_ROLES} from "../../shared/auth/roles";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {useTranslation} from "../../shared/i18n";
import {createBranch, loadBranch, loadPlaceProperties, updateBranch, uploadBranchGalleryPhoto} from "./api";
import {BranchForm} from "./components/BranchForm";
import {branchToFormValues, toBranchPayload} from "./contracts";
import {validateGalleryFiles} from "./gallery";
import {invalidateBranchLifecycleQueries} from "./queryInvalidation";
import {useActiveAdminCountryScope} from "./useActiveAdminCountryScope";
import "./branch.css";

const emptyValues = () => ({...branchToFormValues(), password: ""});

const validateBranch = (values, isCreate, t) => {
    const required = [
        ["name", t("orgs.branchEditor.missing.name")],
        ["admin_name", t("orgs.branchEditor.missing.admin")],
        ["phone_number", t("orgs.branchEditor.missing.phone")],
        ["login", t("orgs.branchEditor.missing.login")],
        ["bin_number", t("orgs.branchEditor.missing.bin")],
        ["account_number", t("orgs.branchEditor.missing.account")],
        ["off_percent", t("orgs.branchEditor.missing.discount")],
        ["city", t("orgs.branchEditor.missing.city")],
        ["category", t("orgs.branchEditor.missing.category")],
        ["address", t("orgs.branchEditor.missing.address")],
        ["description", t("orgs.branchEditor.missing.description")],
        ["lat", t("orgs.branchEditor.missing.lat")],
        ["lng", t("orgs.branchEditor.missing.lng")],
    ];
    const missing = required.filter(([field]) => String(values[field] ?? "").trim() === "");
    if (isCreate && !String(values.password || "").trim()) missing.push(["password", t("orgs.branchEditor.missing.password")]);
    if (missing.length) return t("orgs.common.requiredFields", {fields: missing.map(([, label]) => label).join(", ")});

    const discount = Number(values.off_percent);
    if (!Number.isInteger(discount) || discount < 0 || discount > 100) return t("orgs.branchEditor.discount");
    if (!Number.isFinite(Number(values.lat)) || !Number.isFinite(Number(values.lng))) return t("orgs.branchEditor.coords");

    const reward = values.personal_reward_percent;
    if (reward !== "" && (!Number.isFinite(Number(reward)) || Number(reward) < 0 || Number(reward) > 100)) {
        return t("orgs.branchEditor.reward");
    }
    const windowMinutes = values.staff_attribution_window_minutes;
    if (windowMinutes !== "" && windowMinutes !== null && (!Number.isInteger(Number(windowMinutes)) || Number(windowMinutes) < 0)) {
        return t("orgs.branchEditor.window");
    }
    return "";
};

const uploadGalleryFiles = async (branchId, photos) => {
    const results = await Promise.allSettled(
        photos.map((photo) => uploadBranchGalleryPhoto({branchId, photo})),
    );
    return results.reduce((failed, result, index) => {
        if (result.status === "rejected") failed.push({photo: photos[index], error: result.reason});
        return failed;
    }, []);
};

export const BranchEditorPage = () => {
    const {t} = useTranslation();
    const {employer_id: placeId, branch_id: branchId} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const capabilities = useCapabilities();
    const {countryScope} = useActiveAdminCountryScope(capabilities);
    const isCreate = !branchId;
    const role = capabilities.user?.role;
    const isOwner = role === ADMIN_ROLES.OWNER;
    const [values, setValues] = useState(emptyValues);
    const [loadedBranchId, setLoadedBranchId] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [validationError, setValidationError] = useState("");
    const [mutationResult, setMutationResult] = useState(null);
    const [createdBranchId, setCreatedBranchId] = useState(null);

    const branchQuery = useQuery({
        queryKey: ["admin-branch", countryScope, branchId],
        queryFn: () => loadBranch(branchId),
        enabled: !isCreate,
        retry: false,
    });
    const propertiesQuery = useQuery({
        queryKey: ["admin-place-properties", countryScope],
        queryFn: loadPlaceProperties,
        retry: false,
    });

    useEffect(() => {
        if (!branchQuery.data) return;
        setValues(branchToFormValues(branchQuery.data));
        setLoadedBranchId(String(branchId));
    }, [branchId, branchQuery.data]);

    const saveMutation = useMutation({
        mutationFn: async ({payload, photos, recoveryBranchId = null}) => {
            const response = recoveryBranchId
                ? null
                : isCreate
                    ? await createBranch(placeId, payload)
                    : await updateBranch(branchId, payload);
            if (isCreate && response?.application_id) {
                return {response, savedBranchId: null, failedUploads: [], recoveryBranchId, applicationId: response.application_id};
            }
            const savedBranchId = recoveryBranchId || response?.id || response?.branch_id || branchId;
            const failedUploads = isCreate && savedBranchId && photos.length
                ? await uploadGalleryFiles(savedBranchId, photos)
                : [];
            return {response, savedBranchId, failedUploads, recoveryBranchId, applicationId: null};
        },
        onSuccess: ({response, savedBranchId, failedUploads, recoveryBranchId, applicationId}) => {
            if (applicationId) {
                void queryClient.invalidateQueries({queryKey: ["admin-onboarding"]});
                setMutationResult({
                    kind: "success",
                    message: response?.message || t("orgs.branchEditor.moderationSent"),
                });
                navigate(`/business-onboarding/${applicationId}`);
                return;
            }
            invalidateBranchLifecycleQueries(queryClient, {placeId, branchId: savedBranchId});
            if (failedUploads.length) {
                const mapped = mapApiError(failedUploads[0].error);
                setCreatedBranchId(savedBranchId);
                setGalleryFiles(failedUploads.map(({photo}) => photo));
                setMutationResult({
                    kind: "error",
                    message: t("orgs.branchEditor.photosPartial", {message: mapped.message}),
                });
                return;
            }
            setGalleryFiles([]);
            setMutationResult({
                kind: "success",
                message: recoveryBranchId
                    ? t("orgs.branchEditor.photosUploaded")
                    : response?.message || (isCreate ? t("orgs.branchEditor.created") : isOwner ? t("orgs.branchEditor.requestSent") : t("orgs.branchEditor.saved")),
            });
            if (isOwner && !isCreate) return;
            navigate(isOwner ? "/employers" : `/employers/${placeId}`);
        },
        onError: (error) => {
            const mapped = mapApiError(error);
            setMutationResult({kind: "error", message: mapped.message});
        },
    });

    if (!capabilities.hasAction("organization.manage")) {
        return <DataState title={t("orgs.common.editUnavailable")} copy={t("orgs.branchEditor.noPermission")} />;
    }
    if (!isCreate && branchQuery.isLoading) {
        return <DataState title={t("orgs.branchEditor.loadingTitle")} copy={t("orgs.branchEditor.loadingCopy")} />;
    }
    if (!isCreate && branchQuery.isError) {
        return <DataState title={t("orgs.branchEditor.errorTitle")} copy={t("orgs.branchEditor.errorCopy")} />;
    }
    if (propertiesQuery.isLoading || (!isCreate && loadedBranchId !== String(branchId))) {
        return <DataState title={t("orgs.common.preparingEditor")} copy={t("orgs.branchEditor.preparingCopy")} />;
    }

    const properties = propertiesQuery.data || {};
    const cities = properties.cities || properties.filterCities || [];
    const categories = properties.sub_categories || properties.categories || properties.filterCategories || [];
    const positions = properties.positions || [];
    const title = isCreate
        ? t("orgs.branchEditor.createTitle")
        : isOwner
            ? t("orgs.common.changeNamed", {name: values.name || t("orgs.common.branchGenitive")})
            : t("orgs.common.editNamedTitle", {name: values.name || t("orgs.common.branchGenitiveNumber", {id: branchId})});
    const submitLabel = isCreate
        ? (role === ADMIN_ROLES.DEPLOYER || role === ADMIN_ROLES.IMPLEMENTATION_HEAD || isOwner ? t("orgs.common.sendToModeration") : t("orgs.branchEditor.create"))
        : isOwner ? t("orgs.common.sendRequest") : t("orgs.common.saveChanges");

    const resetFeedback = () => {
        setValidationError("");
        setMutationResult(null);
    };

    const handleChange = (event) => {
        const {name, type, checked, value} = event.target;
        resetFeedback();
        setValues((current) => ({...current, [name]: type === "checkbox" ? checked : value}));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const error = createdBranchId ? "" : validateBranch(values, isCreate, t);
        if (error) {
            setValidationError(error);
            return;
        }
        resetFeedback();
        saveMutation.mutate({
            payload: toBranchPayload(values, {mode: isCreate ? "create" : "update"}),
            photos: galleryFiles,
            recoveryBranchId: createdBranchId,
        });
    };

    const handleGalleryChange = (event) => {
        const files = Array.from(event.target.files || []);
        const error = validateGalleryFiles(files);
        setMutationResult(null);
        if (error) {
            setGalleryFiles([]);
            setValidationError(error);
            return;
        }
        setValidationError("");
        setGalleryFiles(files);
    };

    return (
        <div className="admin-page branch-editor-page">
            <header className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">{title}</h1>
                    <p className="admin-page-copy">
                        {isOwner && !isCreate
                            ? t("orgs.branchEditor.ownerCopy")
                            : t("orgs.branchEditor.adminCopy")}
                    </p>
                </div>
                <div className="admin-pill"><GitBranch aria-hidden="true" size={14} /> {isCreate ? t("orgs.branchEditor.creating") : t("orgs.branchEditor.editing")}</div>
            </header>
            {propertiesQuery.isError ? (
                <DataState title={t("orgs.common.dictionariesTitle")} copy={t("orgs.branchEditor.dictionariesCopy")} />
            ) : (
                <>
                    <MutationResultBanner result={mutationResult} />
                    {createdBranchId ? (
                        <div className="branch-created-link">
                            <Link className="admin-button admin-button-primary" to={`/employers/${placeId}/branch/${createdBranchId}`}>
                                {t("orgs.branchEditor.openCreated")}
                            </Link>
                        </div>
                    ) : null}
                    <BranchForm
                        values={values}
                        cities={cities}
                        categories={categories}
                        positions={positions}
                        galleryFiles={galleryFiles}
                        isCreate={isCreate}
                        pending={saveMutation.isPending}
                        validationError={validationError}
                        submitLabel={createdBranchId ? t("orgs.branchEditor.retryPhotos") : submitLabel}
                        onChange={handleChange}
                        onGalleryChange={handleGalleryChange}
                        onAddStaff={(staffMember) => {
                            resetFeedback();
                            setValues((current) => ({...current, personal: [...current.personal, staffMember]}));
                        }}
                        onRemoveStaff={(index) => {
                            resetFeedback();
                            setValues((current) => ({...current, personal: current.personal.filter((_, itemIndex) => itemIndex !== index)}));
                        }}
                        onSubmit={handleSubmit}
                    />
                </>
            )}
        </div>
    );
};
